import type {
  TicketRelationshipSummary,
  TicketStatus,
  TicketSubtask,
} from '../../store/features/tickets/types';

export type TicketEditorUiPriority = 'low' | 'medium' | 'high' | 'urgent';

export type TicketEditorTicket = {
  id: string;
  orgId?: string | null;
  ticketNumber?: string | null;
  title: string;
  createdAt?: any;
  description?: string;
  projectId: string;
  milestoneId?: string | null;
  milestoneTitle?: string | null;
  teamId?: string | null;
  owningTeamNodeId?: string | null;
  currentTeamNodeId?: string | null;
  currentTeamLabel?: string | null;
  defaultTeamNodeId?: string | null;
  defaultTeamLabel?: string | null;
  serviceId?: string | null;
  tierId?: string | null;
  offeringId?: string | null;
  offeringSubcategoryId?: string | null;
  kbaseProcessId?: string | null;
  clientSubscriptionId?: string | null;
  serviceCode?: string | null;
  serviceTitle?: string | null;
  tierSkuId?: string | null;
  tierName?: string | null;
  offeringCode?: string | null;
  offeringName?: string | null;
  offeringSubcategoryName?: string | null;
  offeringSubcategoryDescription?: string | null;
  estimatedMinutes?: number | null;
  remainingEstimateMinutes?: number | null;
  targetCompletionMinutes?: number | null;
  actualActiveMinutes?: number | null;
  pausedMinutes?: number | null;
  priority?: TicketEditorUiPriority;
  assignedAgentId?: string | null;
  assignedAgentFirstName?: string | null;
  assignedAgentLastName?: string | null;
  assignedAgentDisplayName?: string | null;
  assignedAgentEmail?: string | null;
  assignedAgentPhotoURL?: string | null;
  assignedOrgMemberId?: string | null;
  assignedOrgMemberFirstName?: string | null;
  assignedOrgMemberLastName?: string | null;
  assignedOrgMemberDisplayName?: string | null;
  assignedOrgMemberEmail?: string | null;
  assignedOrgMemberPhotoURL?: string | null;
  plannedStartAt?: any;
  plannedEndAt?: any;
  timezone?: string | null;
  slaResponseDueAt?: any;
  slaResolutionDueAt?: any;
  clientId?: string;
  clientBusinessName?: string | null;
  clientBusinessLogoURL?: string | null;
  clientFirstName?: string | null;
  clientLastName?: string | null;
  clientDisplayName?: string | null;
  clientEmail?: string | null;
  clientPhotoURL?: string | null;
  createdById?: string | null;
  subtasks?: TicketSubtask[];
  status?: TicketStatus;
  blockedBy?: TicketRelationshipSummary[];
  blocks?: TicketRelationshipSummary[];
  handoffTo?: TicketRelationshipSummary[];
  handoffFrom?: TicketRelationshipSummary[];
  participants?: string[];
  subscriptionOptions?: any[];
  subscriptionSelectionRequired?: boolean;
  subscriptionResolutionError?: string | null;
  offeringOptions?: any[];
  offeringSelectionRequired?: boolean;
  teamOptions?: any[];
  projects?: { id: string; name: string }[];
  onDelete?: (id: string) => void | Promise<void>;
  onAttach?: (id: string, file: File) => void;
};

type TicketLike = Record<string, any> & { id?: unknown };

type BuildTicketEditorTicketOptions = {
  fallbackProjectId?: string | null;
  onDelete?: (id: string) => void | Promise<void>;
  onAttach?: (id: string, file: File) => void;
};

function stringOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function stringOrUndefined(value: unknown): string | undefined {
  return stringOrNull(value) ?? undefined;
}

function normalizeProjects(value: unknown): Array<{ id: string; name: string }> | undefined {
  if (!Array.isArray(value)) return undefined;
  const projects = value
    .map((entry) => {
      const id = stringOrNull((entry as any)?.id);
      const name =
        stringOrNull((entry as any)?.name) ||
        stringOrNull((entry as any)?.title) ||
        stringOrNull((entry as any)?.code);
      if (!id) return null;
      return { id, name: name || id };
    })
    .filter(Boolean) as Array<{ id: string; name: string }>;
  return projects.length ? projects : undefined;
}

function normalizeParticipants(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => stringOrNull(entry))
    .filter((entry): entry is string => Boolean(entry));
}

function normalizeRelationshipSummaries(value: unknown): TicketRelationshipSummary[] {
  if (!Array.isArray(value)) return [];
  const normalized: TicketRelationshipSummary[] = [];
  for (const entry of value) {
    const id = stringOrNull((entry as any)?.id);
    const title = stringOrNull((entry as any)?.title);
    if (!id || !title) continue;
    normalized.push({
      id,
      ticketNumber: stringOrNull((entry as any)?.ticketNumber),
      title,
      status: stringOrUndefined((entry as any)?.status) as TicketStatus | undefined,
      priority: stringOrUndefined((entry as any)?.priority) as any,
      projectId: stringOrNull((entry as any)?.projectId),
      milestoneId: stringOrNull((entry as any)?.milestoneId),
      owningTeamNodeId: stringOrNull((entry as any)?.owningTeamNodeId),
      defaultTeamNodeId: stringOrNull((entry as any)?.defaultTeamNodeId),
      serviceId: stringOrNull((entry as any)?.serviceId),
      clientSubscriptionId: stringOrNull((entry as any)?.clientSubscriptionId),
      offeringId: stringOrNull((entry as any)?.offeringId),
      offeringSubcategoryId: stringOrNull((entry as any)?.offeringSubcategoryId),
    });
  }
  return normalized;
}

export function mapTicketPriorityToEditorUi(priority?: unknown): TicketEditorUiPriority {
  switch (priority) {
    case 'P0':
    case 'urgent':
      return 'urgent';
    case 'P1':
    case 'high':
      return 'high';
    case 'P3':
    case 'low':
      return 'low';
    case 'P2':
    case 'medium':
    default:
      return 'medium';
  }
}

export function buildTicketEditorTicket(
  source: TicketLike | null | undefined,
  options: BuildTicketEditorTicketOptions = {},
): TicketEditorTicket | null {
  if (!source?.id) return null;

  return {
    id: String(source.id),
    orgId: stringOrNull(source.orgId),
    ticketNumber: stringOrNull(source.ticketNumber),
    title: String(source.title ?? ''),
    createdAt: source.createdAt,
    description: source.description ?? '',
    projectId: stringOrNull(source.projectId) || stringOrNull(options.fallbackProjectId) || '',
    milestoneId: stringOrNull(source.milestoneId),
    milestoneTitle: stringOrNull(source.milestoneTitle),
    teamId: stringOrNull(source.teamId),
    owningTeamNodeId: stringOrNull(source.owningTeamNodeId),
    currentTeamNodeId: stringOrNull(source.currentTeamNodeId),
    currentTeamLabel: stringOrNull(source.currentTeamLabel),
    defaultTeamNodeId: stringOrNull(source.defaultTeamNodeId),
    defaultTeamLabel: stringOrNull(source.defaultTeamLabel),
    serviceId: stringOrNull(source.serviceId),
    tierId: stringOrNull(source.tierId),
    offeringId: stringOrNull(source.offeringId),
    offeringSubcategoryId: stringOrNull(source.offeringSubcategoryId),
    kbaseProcessId: stringOrNull(source.kbaseProcessId),
    clientSubscriptionId: stringOrNull(source.clientSubscriptionId),
    serviceCode: stringOrNull(source.serviceCode),
    serviceTitle: stringOrNull(source.serviceTitle),
    tierSkuId: stringOrNull(source.tierSkuId),
    tierName: stringOrNull(source.tierName),
    offeringCode: stringOrNull(source.offeringCode),
    offeringName: stringOrNull(source.offeringName),
    offeringSubcategoryName: stringOrNull(source.offeringSubcategoryName),
    offeringSubcategoryDescription: stringOrNull(source.offeringSubcategoryDescription),
    estimatedMinutes:
      typeof source.estimatedMinutes === 'number' ? source.estimatedMinutes : source.estimatedMinutes ?? null,
    remainingEstimateMinutes:
      typeof source.remainingEstimateMinutes === 'number'
        ? source.remainingEstimateMinutes
        : source.remainingEstimateMinutes ?? null,
    targetCompletionMinutes:
      typeof source.targetCompletionMinutes === 'number'
        ? source.targetCompletionMinutes
        : source.targetCompletionMinutes ?? null,
    actualActiveMinutes:
      typeof source.actualActiveMinutes === 'number' ? source.actualActiveMinutes : source.actualActiveMinutes ?? null,
    pausedMinutes:
      typeof source.pausedMinutes === 'number' ? source.pausedMinutes : source.pausedMinutes ?? null,
    priority: mapTicketPriorityToEditorUi(source.priority),
    assignedAgentId: stringOrNull(source.assignedAgentId),
    assignedAgentFirstName: stringOrNull(source.assignedAgentFirstName),
    assignedAgentLastName: stringOrNull(source.assignedAgentLastName),
    assignedAgentDisplayName: stringOrNull(source.assignedAgentDisplayName),
    assignedAgentEmail: stringOrNull(source.assignedAgentEmail),
    assignedAgentPhotoURL: stringOrNull(source.assignedAgentPhotoURL),
    assignedOrgMemberId: stringOrNull(source.assignedOrgMemberId),
    assignedOrgMemberFirstName: stringOrNull(source.assignedOrgMemberFirstName),
    assignedOrgMemberLastName: stringOrNull(source.assignedOrgMemberLastName),
    assignedOrgMemberDisplayName: stringOrNull(source.assignedOrgMemberDisplayName),
    assignedOrgMemberEmail: stringOrNull(source.assignedOrgMemberEmail),
    assignedOrgMemberPhotoURL: stringOrNull(source.assignedOrgMemberPhotoURL),
    plannedStartAt: source.plannedStartAt,
    plannedEndAt: source.plannedEndAt,
    timezone: stringOrNull(source.timezone),
    slaResponseDueAt: source.slaResponseDueAt,
    slaResolutionDueAt: source.slaResolutionDueAt,
    clientId: stringOrUndefined(source.clientId),
    clientBusinessName: stringOrNull(source.clientBusinessName),
    clientBusinessLogoURL: stringOrNull(source.clientBusinessLogoURL),
    clientFirstName: stringOrNull(source.clientFirstName),
    clientLastName: stringOrNull(source.clientLastName),
    clientDisplayName: stringOrNull(source.clientDisplayName),
    clientEmail: stringOrNull(source.clientEmail),
    clientPhotoURL: stringOrNull(source.clientPhotoURL),
    createdById:
      stringOrNull(source.createdById) ||
      stringOrNull(source.createdBy) ||
      stringOrNull(source.clientId) ||
      stringOrNull(source.authorId),
    subtasks: Array.isArray(source.subtasks) ? (source.subtasks as TicketSubtask[]) : [],
    status: source.status as TicketStatus | undefined,
    blockedBy: normalizeRelationshipSummaries(source.blockedBy),
    blocks: normalizeRelationshipSummaries(source.blocks),
    handoffTo: normalizeRelationshipSummaries(source.handoffTo),
    handoffFrom: normalizeRelationshipSummaries(source.handoffFrom),
    participants: normalizeParticipants(source.participants ?? source.secondaryAgentIds),
    subscriptionOptions: Array.isArray(source.subscriptionOptions) ? source.subscriptionOptions : undefined,
    subscriptionSelectionRequired:
      typeof source.subscriptionSelectionRequired === 'boolean' ? source.subscriptionSelectionRequired : undefined,
    subscriptionResolutionError: stringOrNull(source.subscriptionResolutionError),
    offeringOptions: Array.isArray(source.offeringOptions) ? source.offeringOptions : undefined,
    offeringSelectionRequired:
      typeof source.offeringSelectionRequired === 'boolean' ? source.offeringSelectionRequired : undefined,
    teamOptions: Array.isArray(source.teamOptions) ? source.teamOptions : undefined,
    projects: normalizeProjects(source.projects),
    onDelete: options.onDelete ?? source.onDelete,
    onAttach: options.onAttach ?? source.onAttach,
  };
}
